// bp-cal-roster-v1 — the SINGLE source of truth for turning D1 `users` rows into
// the calendar roster shape shared by the worker (GET /api/calendar/users, the
// events/freebusy fan-out, appointment ownership) and the frontend calendar view.
//
// Historically the roster was a hand-maintained hardcoded array in TWO places
// (api/src/index.ts CALENDAR_USERS + src/App.tsx CALENDAR_USERS_RAW/MAP) that had
// to be kept in lockstep. That broke the spec — "anyone who logs in with a
// @botpros.ai domain has calendar access" — because a new teammate never appeared
// in the roster until someone hand-edited both files. This module derives the
// roster from the live `users` table instead. Pure + framework-agnostic so it
// typechecks under BOTH the Cloudflare workers-types config and the browser
// config, and is unit-tested by vitest (src/**/*.test.ts).

export const BOTPROS_CALENDAR_DOMAIN = '@botpros.ai'

export type CalendarUser = {
  email: string
  userId: number
  section: number
  name: string
  role: string
}

// A raw D1 `users` row — the columns the roster loader selects.
export type CalendarRosterRow = {
  id: number
  email: string | null
  name: string | null
  role: string | null
  is_admin?: number | null
}

// Roles that sort to the FRONT of the roster (admins first, then alpha). Mirrors
// the admin-tier detection used elsewhere in the worker (resolveAuthMiddlewareRole).
const ADMIN_ROSTER_ROLES = new Set([
  'admin', 'super_admin', 'super admin', 'superadmin', 'owner', 'consultant', 'vp',
])

export function isBotprosCalendarEmail(email: string | null | undefined): boolean {
  return !!email && email.trim().toLowerCase().endsWith(BOTPROS_CALENDAR_DOMAIN)
}

export function isAdminRosterRow(row: Pick<CalendarRosterRow, 'role' | 'is_admin'>): boolean {
  if (row.is_admin === 1) return true
  const r = (row.role || '').toLowerCase().trim()
  return ADMIN_ROSTER_ROLES.has(r)
}

// Title-case the local part of an email as a last-resort display name.
export function nameFromCalendarEmail(email: string): string {
  const local = (email.split('@')[0] || '').replace(/[._-]+/g, ' ').trim()
  if (!local) return email
  return local
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

// Derive 1-2 char initials from a display name ("Drew BotPros" -> "DB", "Max" -> "MA").
export function initialsFromName(name: string): string {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

// Build the deterministic calendar roster from D1 users rows.
//   - keeps only @botpros.ai mailboxes (the loader ALSO filters is_active=1 and
//     the domain in SQL; this is defence-in-depth + the unit-test contract),
//   - dedupes by userId (first wins) and by lowercased email,
//   - orders admins first, then case-insensitive alpha by name (stable by id),
//   - normalizes email to lowercase (matches the find-by-email comparisons the
//     worker does everywhere and the mailbox Google DWD impersonates),
//   - `section` is retained for wire-compat with the CalendarUser shape and is 0
//     for every entry (no frontend lane logic branches on it).
export function buildCalendarRoster(rows: readonly CalendarRosterRow[]): CalendarUser[] {
  const seenIds = new Set<number>()
  const seenEmails = new Set<string>()
  const prepared: Array<{ user: CalendarUser; admin: boolean }> = []
  for (const row of rows || []) {
    const id = Number(row?.id)
    if (!Number.isFinite(id) || id <= 0) continue
    const email = (row.email || '').trim().toLowerCase()
    if (!isBotprosCalendarEmail(email)) continue
    if (seenIds.has(id) || seenEmails.has(email)) continue
    seenIds.add(id)
    seenEmails.add(email)
    const name = (row.name || '').trim() || nameFromCalendarEmail(email)
    const role = (row.role || '').trim() || 'user'
    prepared.push({
      user: { email, userId: id, section: 0, name, role },
      admin: isAdminRosterRow(row),
    })
  }
  prepared.sort((a, b) => {
    if (a.admin !== b.admin) return a.admin ? -1 : 1
    const byName = a.user.name.toLowerCase().localeCompare(b.user.name.toLowerCase())
    if (byName !== 0) return byName
    return a.user.userId - b.user.userId
  })
  return prepared.map((p) => p.user)
}
